/**
 * @file   src\components\GitchaAvailableLocationsScreen.js
 * @brief  This file is responsible for showing gitcha available locations in home page
 * @date   May, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { useEffect, useState } from "react";
import {
  getGeoLocation,
  getGitchaAvailableStates,
} from "../services/commonServices";
import { toTitleCase, setLocalStorage } from "../helpers/common";
import CityTile from "../components/Common/CityTile";
import { useDispatch, useSelector } from "react-redux";
import { fetchGitchaAvailableCities } from "../actions/locations";
import { getLocalStorage } from "../helpers/common";
import { isMlsRestrictedTenant } from "../helpers/mlsCityFilter";
import {
  FEATURED_CITIES,
  isFeaturedCity,
  isHiddenCity,
} from "../constants/cityConfig";

const GitchaAvailableLocations = (props) => {
  const [availableStates, setAvailableStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const availableCities = useSelector(
    (state) => state.locations.availableCities,
  );

  const dispatch = useDispatch();
  useEffect(() => {
    fetchGitchaAvailableStatesData();
  }, []);

  const fetchGitchaAvailableStatesData = async () => {
    await getGitchaAvailableStates()
      .then((response) => {
        const res = _.get(response, "result", []);
        setAvailableStates(res);
        res.map((item) => handleLocationState(item.StateCode));
      })
      .catch((error) => console.log(error));
  };

  const handleLocationSelect = async (city, state) => {
    const reqPayload = `address=${city},${state}`;
    await getGeoLocation(reqPayload).then((data) => {
      const results = _.get(data, "results");
      if (results?.length > 0) {
        const address = results[0];
        const geometry = _.get(address, "geometry", "");
        const locBounds = _.get(geometry, "location", "");
        const lat = _.get(locBounds, "lat", "");
        const long = _.get(locBounds, "lng", "");
        const locationData = {
          latitude: lat,
          longitude: long,
          city: city,
          state: state,
          formatted_address: toTitleCase(city) + ", " + toTitleCase(state),
          isFromAvailableCities: true,
        };
        setLocalStorage("searchedLocation", locationData);
        setLocalStorage("userLocation", locationData);
        props.setUserLocation({
          latitude: lat,
          longitude: long,
          city: city,
          state: state,
          formatted_address: toTitleCase(city) + ", " + toTitleCase(state),
          isFromAvailableCities: true,
        });
      }
    });
  };

  const handleLocationState = (stateCode) => {
    if (stateCode) {
      const stateCodeSelected = selectedStates.indexOf(stateCode);
      if (stateCodeSelected == -1) {
        setSelectedStates([...selectedStates, stateCode]);
        dispatch(fetchGitchaAvailableCities({ state: stateCode }));
      }
    }
  };

  // Get cities for a state, excluding featured and hidden ones
  const getCitiesForState = (stateCode) => {
    const selectedState = availableCities?.find(
      (cities) => cities.state === stateCode,
    );
    const cities = selectedState?.cities || [];
    return cities.filter(
      (loc) =>
        !isFeaturedCity(loc.City, stateCode) &&
        !isHiddenCity(loc.City, stateCode)
    );
  };

  return (
    <div className="available-locations">
      <div className="available-locations__header">
        <h2>Explore Available Markets</h2>
        <p>
          {/* [US-011] Avoid "Gitcha" branding for BLS tenants */}
          {isMlsRestrictedTenant()
            ? "Browse want listings in the following cities."
            : "Gitcha is live in the following cities. Click any to browse want listings."}
        </p>
      </div>

      {/* Featured markets - photo cards */}
      <div className="available-locations__featured">
        {FEATURED_CITIES.map((fc, i) => (
          <CityTile
            key={`featured-${i}`}
            city={fc.city}
            state={fc.state}
            image={fc.image}
            onClick={handleLocationSelect}
          />
        ))}
      </div>

      {/* State sections with clean text lists */}
      {availableStates?.length > 0 &&
        availableStates.map((st, index) => {
          const cities = getCitiesForState(st.StateCode);
          if (cities.length === 0) return null;
          return (
            <div
              key={index}
              className="available-locations__state-section"
            >
              <h3 className="available-locations__state-name">
                {st.State}
              </h3>
              <div className="available-locations__city-list">
                {cities.map((loc, ci) => (
                  <a
                    key={ci}
                    className="available-locations__city-link"
                    role="button"
                    href={void 0}
                    onClick={() =>
                      handleLocationSelect(toTitleCase(loc.City), st.StateCode)
                    }
                  >
                    {toTitleCase(loc.City)}
                  </a>
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default GitchaAvailableLocations;
